import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Button from "../ui/Button";
import { ThemeToggle } from "../ui/ThemeToggle";
import { useAuth } from "../../context/AuthContext";
import { authFetch } from "../../lib/authFetch";

type SellerTopbarProps = {
  collapsed: boolean;
  onToggleSidebar: () => void;
};

type CurrentUserResponse = {
  ok?: boolean;
  user?: {
    id?: number | string;
    username?: string;
    first_name?: string;
    last_name?: string;
    rol?: string;
  };
  error?: string;
};

const wrapperStyle: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 20,
  padding: "14px 20px 0 20px",
};

const barStyle: React.CSSProperties = {
  minHeight: "60px",
  border: "1px solid var(--border)",
  borderRadius: "18px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  backdropFilter: "var(--glass)",
  WebkitBackdropFilter: "var(--glass)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "14px",
  padding: "10px 14px",
};

const leftStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  minWidth: 0,
};

const titleWrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: "19px",
  fontWeight: 800,
  color: "var(--text-primary)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.15,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "var(--text-secondary)",
  marginTop: "2px",
};

const rightStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexShrink: 0,
};

const profileChipStyle: React.CSSProperties = {
  minHeight: "38px",
  padding: "0 12px",
  borderRadius: "14px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "13px",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const compactButtonStyle: React.CSSProperties = {
  minHeight: "38px",
  minWidth: "38px",
  padding: "8px 10px",
  borderRadius: "14px",
};

const getPageSubtitle = (pathname: string): string => {
  if (pathname === "/seller") return "Resumen operativo";
  if (pathname === "/seller/envios") return "Seguimiento de envíos";
  return "Área seller";
};

const buildFirstName = (firstName?: string, username?: string | null) => {
  const cleanFirstName = String(firstName || "").trim();
  const cleanUsername = String(username || "").trim();

  return cleanFirstName || cleanUsername || "Seller";
};

const SellerTopbar = ({ collapsed, onToggleSidebar }: SellerTopbarProps) => {
  const location = useLocation();
  const { token, userId, firstName, username, logout } = useAuth();

  const [resolvedFirstName, setResolvedFirstName] = useState<string>(
    buildFirstName(firstName || "", username)
  );

  useEffect(() => {
    let cancelled = false;

    const fetchCurrentUser = async () => {
      if (!token || !userId) {
        setResolvedFirstName(buildFirstName(firstName || "", username));
        return;
      }

      try {
        const response = await authFetch(`/v2/users/${userId}`, { token });
        const payload = (await response.json()) as CurrentUserResponse;

        if (!response.ok || !payload?.user) {
          throw new Error(payload?.error || "No se pudo obtener el usuario actual.");
        }

        const name = buildFirstName(
          payload.user.first_name || "",
          payload.user.username || username
        );

        if (!cancelled) {
          setResolvedFirstName(name);
        }
      } catch (error) {
        console.error(error);

        if (!cancelled) {
          setResolvedFirstName(buildFirstName(firstName || "", username));
        }
      }
    };

    fetchCurrentUser();

    return () => {
      cancelled = true;
    };
  }, [token, userId, firstName, username]);

  const pageSubtitle = useMemo(() => getPageSubtitle(location.pathname), [location.pathname]);

  return (
    <div style={wrapperStyle}>
      <div style={barStyle}>
        <div style={leftStyle}>
          <div title={collapsed ? "Expandir menú" : "Contraer menú"}>
  <Button
    variant="secondary"
    onClick={onToggleSidebar}
    style={compactButtonStyle}
  >
    {collapsed ? "→" : "←"}
  </Button>
</div>

          <div style={titleWrapStyle}>
            <span style={titleStyle}>{`Hola, ${resolvedFirstName}`}</span>
            <span style={subtitleStyle}>{pageSubtitle}</span>
          </div>
        </div>

        <div style={rightStyle}>
          <ThemeToggle />

          <button
            type="button"
            style={profileChipStyle}
            title="Perfil"
            aria-label="Perfil"
          >
            ⚙
          </button>

          <Button variant="ghost" onClick={logout} style={compactButtonStyle}>
            Salir
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SellerTopbar;